import { Grid } from '../common/Grid';
import React, { useState, useEffect } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Box, Typography, CircularProgress, Alert, Tabs, Tab, Stack, IconButton, Chip, Paper, TextField } from '@mui/material';
import {
  Print as PrintIcon,
  Close as CloseIcon,
  Medication as MedicationIcon,
  CheckCircle as CheckIcon,
  Error as ErrorIcon,
  PrintOutlined as ThermalIcon,
  Receipt as ReceiptIcon,
  LocalPostOffice as DeliveryIcon
} from '@mui/icons-material';
import { printingService } from '../../services/printingService';
import { printThermalLabel, ThermalLabelData } from './ThermalLabelPrint';
import { printThermalReceipt, ThermalReceiptData } from './ThermalReceiptPrint';
import { printDeliveryLabel, DeliveryLabelData } from './DeliveryLabelPrint';
import api from '../../services/api';
import { usePharmacyStore } from '../../stores/pharmacyStore';
import { splitLabelsForPrint, maxVialLabelCount, MAX_VIAL_LABELS } from '../../utils/packagingVerificationHelpers';
import type { LabelSplit } from '../../types/packagingVerification.types';
import useZebraPrinters from '../../hooks/useZebraPrinters';
import { resolveFulfillmentType, fulfillmentLabelHeading } from '../../utils/fulfillmentType';
import {
  generateVialLabelZpl,
  generateReceiptZpl,
  generateDeliveryLabelZpl,
  VialLabelPrinterConfig,
} from '../../utils/zplTemplates';

// Re-export types for external use by useAutoPrint hook
export type { ThermalLabelData } from './ThermalLabelPrint';
export type { ThermalReceiptData } from './ThermalReceiptPrint';
export type { DeliveryLabelData } from './DeliveryLabelPrint';

// Export print functions for direct invocation by useAutoPrint hook (T005)
export { printThermalLabel } from './ThermalLabelPrint';
export { printThermalReceipt } from './ThermalReceiptPrint';
export { printDeliveryLabel } from './DeliveryLabelPrint';

/**
 * Load saved printer configuration from localStorage.
 * Aliased from the shared vialPrinterConfig module so every print path
 * and the ZplTestPage use the SAME storage key and the SAME defaults
 * (2" @ 203dpi, centered, POI, margins T-37/L5).
 */
import { loadSavedVialPrinterConfig as loadSavedPrinterConfig } from '../../utils/vialPrinterConfig';
export { loadSavedPrinterConfig };

interface LabelPrintingModalProps {
  open: boolean;
  onClose: () => void;
  orderId?: string;
  orderNumber?: string;
  patientName?: string;
  medications?: Array<{
    rxNumber: string;
    medication: string;
    strength: string;
    quantity: string;
    directions: string;
    prescriber: string;
    din?: string;
    upc?: string;
    packCount?: number;
    /**
     * PRX-18: the drug's per-drug label split rule. With one, label i of the
     * printed count carries its own share (splitLabelsForPrint) instead of the
     * full quantity. Absent/null: every label shows the full quantity, as before.
     */
    labelSplit?: LabelSplit | null;
    /**
     * PRX-18: the quantity dispensed on this label set (a short fill or a short
     * balance top-up dispenses less than `quantity`). Required with a
     * `labelSplit`: the split is of what is in the bottles. A split drug
     * without it prints nothing and says so.
     */
    dispensedQuantity?: number;
    /** GP-665: partial/balance fill fields merged into the printed vial label */
    balance?: Pick<ThermalLabelData, 'amountSent' | 'prescribedQty' | 'balanceIndicator'>;
    /**
     * GP-1008/GP-1322 reprint parity — what THIS Rx was packaged/verified with,
     * supplied by the caller from `order.metadata.packagingData` + its
     * product-verification signature (Kroll carries none of it). Per medication,
     * not per order: on a multi-Rx order each drug has its own lot/expiry and its
     * own verifier — each Rx is packaged from its own stock bottle — and one
     * drug's values on another drug's label would be wrong, not a fallback.
     * Absent when the drug has no packaging record — the reprint then behaves
     * exactly as it did before, rather than printing invented values. When
     * present these win for this medication; the modal-level props remain the
     * single-drug default (Packaging prints one drug at a time).
     */
    lotNumber?: string;
    expiryDate?: string;
    /** GP-1007: every distinct lot/expiry when the drug came from multiple lots. */
    lotNumbers?: Array<{ lot: string; expiry: string }>;
    /** GP-654/GP-1008: name on the receipt's "Signature" line. */
    productVerifierName?: string;
  }>;
  deliveryAddress?: {
    street: string;
    unit?: string; // GP-564: Unit / suite number for the delivery label
    city: string;
    province: string;
    postalCode: string;
    deliveryInstructions?: string; // GP-887: unit notes / delivery notes line
  };
  /**
   * GP-1234: fulfillment type — 'pickup' | 'delivery' (order.deliveryMethod).
   * This is the field the Patient 360 Order History "Delivery" column renders;
   * it drives the PICKUP/DELIVERY badge on the printed label and this dialog's
   * heading. Distinct from `deliveryType`, which is the courier routing and is
   * null on pickup orders.
   */
  deliveryMethod?: string;
  deliveryType?: string; // GP-333: Delivery type (InHouse, Courier, 3rd_Party, etc.)
  shipmentDate?: string; // GP-333: Shipment/delivery date (YYYY-MM-DD)
  lotNumber?: string;
  expiryDate?: string;
  /**
   * GP-521/GP-664/GP-1317: Kroll nursing-home group name (`metadata.krollGroup.groupName`).
   * Printed on the delivery tag only — never the vial label. GP-1317: this prop did
   * not exist, so both label paths that go through this dialog (the manual print and
   * the auto-print fallback in Packaging) printed a nameless tag that staff could not
   * sort by home. Absent — never defaulted — for non-NH orders and when the caller
   * has no group stamp: an NH tag with the wrong home is worse than one with none.
   */
  nhGroupName?: string;
  /**
   * GP-1008: the product verifier's full name for the receipt "Signature" line
   * (GP-654). On a REPRINT from Patient 360 this must be supplied by the caller —
   * the Kroll receipt payload has no verifier — so the reprinted receipt keeps
   * the verifier initials the original print carried instead of a blank line.
   */
  productVerifierName?: string;
  /**
   * PRX-18: staff changed "# Labels" on a drug with a split rule. Packaging
   * routes it into its "Labels to print" count, so the change is audited the
   * same way as the stepper (labelSplitOverride on the packaging item).
   */
  onLabelCountChange?: (rxNumber: string, count: number) => void;
}

const LabelPrintingModal: React.FC<LabelPrintingModalProps> = ({
  open,
  onClose,
  orderId,
  orderNumber = 'TEST-001',
  patientName = 'Test Patient',
  medications = [],
  deliveryAddress,
  deliveryMethod,
  deliveryType,
  shipmentDate,
  lotNumber,
  expiryDate,
  nhGroupName,
  productVerifierName,
  onLabelCountChange
}) => {
  const activePharmacy = usePharmacyStore((state: any) => state.activePharmacy);
  // GP-1234: pickup orders must not print/preview as an anonymous "Delivery Label".
  const fulfillment = resolveFulfillmentType(deliveryMethod);
  const labelHeading = fulfillmentLabelHeading(deliveryMethod);
  const [activeTab, setActiveTab] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [printStatus, setPrintStatus] = useState<Record<string, boolean>>({});
  const [krollLabelData, setKrollLabelData] = useState<ThermalLabelData | null>(null);
  const [labelCountOverrides, setLabelCountOverrides] = useState<Record<number, number>>({});

  // Initialize label count overrides from medication packCount
  useEffect(() => {
    const initialCounts: Record<number, number> = {};
    medications.forEach((med, index) => {
      initialCounts[index] = med.packCount || 1;
    });
    setLabelCountOverrides(initialCounts);
  }, [medications]);

  /**
   * PRX-18: the labels a split drug prints, label 1 first, for the count shown
   * in "# Labels". Null for a drug with no split rule. Throws when a split drug
   * has no dispensed quantity: never split a guessed quantity.
   */
  const splitLabelsFor = (medication: (typeof medications)[number], count: number) => {
    if (!medication.labelSplit) return null;
    if (medication.dispensedQuantity == null) {
      throw new Error(`Cannot split the labels for Rx ${medication.rxNumber}: the dispensed quantity is missing`);
    }
    return splitLabelsForPrint(medication.labelSplit, count, medication.dispensedQuantity);
  };

  /** PRX-18: the most labels "# Labels" allows; a split drug stops where no label would print 0. */
  const maxLabelsFor = (medication: (typeof medications)[number]) =>
    medication.labelSplit && medication.dispensedQuantity != null
      ? maxVialLabelCount(medication.labelSplit, medication.dispensedQuantity)
      : 20;

  // Zebra printer integration
  const {
    enabled: zebraEnabled,
    isInitialized: zebraInitialized,
    isReady: isZebraReady,
    waitForReady: waitForZebraReady,
    print: zebraPrint,
    browserPrintAvailable,
    getPrintMode,
    getPrinter,
  } = useZebraPrinters();

  const printMode = getPrintMode();

  /**
   * Fetch label data from Kroll via backend
   */
  const fetchLabelDataFromKroll = async (rxNumber: string, lot?: string, expiry?: string): Promise<ThermalLabelData | null> => {
    try {
      const pharmacyId = activePharmacy?.id || '';
      const response = await api.get(`/api/printing/label-data`, {
        params: {
          pharmacyId,
          rxNumber,
          lotNumber: lot,
          expiryDate: expiry
        }
      });

      if (response.data.success) {
        return response.data.data;
      } else {
        throw new Error(response.data.message || 'Failed to fetch label data');
      }
    } catch (error: any) {
      console.error('Error fetching label data from Kroll:', error);
      throw error;
    }
  };

  /**
   * Fetch receipt data from Kroll via backend
   * Uses the new /rx/:pharmacyId/receipt/:rxNum endpoint with optimized query
   */
  const fetchReceiptDataFromKroll = async (rxNumber: string): Promise<ThermalReceiptData | null> => {
    try {
      const pharmacyId = activePharmacy?.id || '';
      if (!pharmacyId) {
        throw new Error('No active pharmacy selected');
      }
      const response = await api.get(`/api/rx/${pharmacyId}/receipt/${rxNumber}`);

      if (response.data.success) {
        const data = response.data.data;
        // Map backend response to ThermalReceiptData interface
        return {
          rxNumber: String(data.rxNumber),
          patientName: data.patientName || '',
          fillDate: data.fillDate || '',
          address1: data.address1 || '',
          address2: data.address2 || '',
          dispQty: data.dispQty || 0,
          brandName: data.brandName || '',
          genericName: data.genericName || '',
          din: data.din ? `DIN: ${data.din}` : '',
          daysSupply: data.daysSupply ? `Days: ${data.daysSupply}` : '',
          refills: String(data.refills || 0),
          doctorName: data.doctorName || '',
          cost: data.cost || 0,
          fee: data.fee || 0,
          total: data.total || 0,
          price: data.price || 0,
          insurancePays: data.insurancePays || 0,
          insurancePlanNames: data.insurancePlanNames || 'Insurance',
          waived: data.waivedAmount || 0,
          patientPhone: data.patientPhone || '',
          manufacturer: data.manufacturer || 'HPH',
          form: data.form || '',
          // Pharmacy info from Kroll
          pharmacyName: data.pharmacyName || '',
          pharmacyAddress: data.pharmacyAddress || '',
          pharmacyCity: data.pharmacyCity || '',
          pharmacyProvince: data.pharmacyProvince || '',
          pharmacyPostalCode: data.pharmacyPostalCode || '',
          pharmacyPhone: data.pharmacyPhone || '',
        };
      } else {
        throw new Error(response.data.message || 'Failed to fetch receipt data');
      }
    } catch (error: any) {
      console.error('Error fetching receipt data from Kroll:', error);
      throw error;
    }
  };

  const handleGenerateVialLabel = async (medication: any, index: number) => {
    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      // Fetch real Kroll data with lot number and expiry date from packaging.
      // GP-1008/GP-1322: prefer THIS drug's packaged values over the order-level
      // props — on a multi-Rx order the two differ, and Kroll echoes back
      // whatever we send.
      const krollData = await fetchLabelDataFromKroll(
        medication.rxNumber,
        medication.lotNumber ?? lotNumber,
        medication.expiryDate ?? expiryDate,
      );

      if (!krollData) {
        throw new Error('No label data found for this Rx number');
      }

      setKrollLabelData(krollData);

      const splitLabels = splitLabelsFor(medication, labelCountOverrides[index] || medication.packCount || 1);
      const packCount = splitLabels?.length ?? (labelCountOverrides[index] || medication.packCount || 1);
      // Wait for Zebra initialization to complete before checking readiness
      const useZebra = await waitForZebraReady('vial');

      if (!useZebra) {
        console.warn('[Print] Zebra not ready for vial, falling back to browser.', {
          enabled: zebraEnabled,
          browserPrintAvailable,
          printerAssigned: !!getPrinter('vial'),
          zebraInitialized,
        });
      }

      // Print thermal label for each pack
      for (let i = 1; i <= packCount; i++) {
        const labelData = {
          ...krollData,
          pharmacyId: activePharmacy?.id,
          ...(medication.balance || {}), // GP-665: "X of Y" + balance indicator
          // GP-1007/GP-1008: a drug packaged from several lots printed EVERY
          // lot/expiry originally; the reprint lists the same ones in the same order.
          ...(medication.lotNumbers && medication.lotNumbers.length > 1
            ? { lotNumbers: medication.lotNumbers }
            : {}),
          packIndex: i,
          totalPacks: packCount,
          ...(splitLabels ? { splitLabel: splitLabels[i - 1] } : {}),
        };

        if (useZebra) {
          // Use Zebra Browser Print with saved printer config
          const printerConfig = loadSavedPrinterConfig();
          const zpl = generateVialLabelZpl(labelData, printerConfig);
          const result = await zebraPrint('vial', zpl);
          if (!result.success) {
            throw new Error(result.error || 'Zebra print failed');
          }
        } else {
          // Fall back to browser print
          printThermalLabel(labelData);
        }

        // Small delay between prints
        if (packCount > 1) await new Promise(resolve => setTimeout(resolve, 500));
      }

      setPrintStatus(prev => ({ ...prev, [`vial-${index}`]: true }));
      const printMethodText = useZebra ? 'Zebra printer' : 'browser';
      setSuccess(`Sent ${packCount} thermal label(s) to ${printMethodText}!`);
    } catch (err: any) {
      console.error('Failed to generate thermal label:', err);
      setError(err.message || 'Failed to generate thermal label');
    } finally {
      setLoading(false);
    }
  };


  const handlePrintAllLabels = async () => {
    setLoading(true);
    setError(null);

    try {
      // Wait for Zebra initialization to complete before checking readiness
      const useZebra = await waitForZebraReady('vial');

      if (!useZebra) {
        console.warn('[Print] Zebra not ready for vial batch, falling back to browser.', {
          enabled: zebraEnabled,
          browserPrintAvailable,
          printerAssigned: !!getPrinter('vial'),
          zebraInitialized,
        });
      }

      // Print thermal labels for all medications
      for (let index = 0; index < medications.length; index++) {
        const medication = medications[index];
        // GP-1008/GP-1322: per-Rx lot/expiry — on a multi-Rx order each label
        // must carry the stock bottle THAT drug was packaged from, not
        // medication[0]'s (see handleGenerateVialLabel).
        const krollData = await fetchLabelDataFromKroll(
          medication.rxNumber,
          medication.lotNumber ?? lotNumber,
          medication.expiryDate ?? expiryDate,
        );

        if (krollData) {
          const splitLabels = splitLabelsFor(medication, medication.packCount || 1);
          const packCount = splitLabels?.length ?? (medication.packCount || 1);
          for (let i = 1; i <= packCount; i++) {
            const labelData = {
              ...krollData,
              pharmacyId: activePharmacy?.id,
              ...(medication.balance || {}), // GP-665: "X of Y" + balance indicator
              ...(medication.lotNumbers && medication.lotNumbers.length > 1
                ? { lotNumbers: medication.lotNumbers }
                : {}),
              packIndex: i,
              totalPacks: packCount,
              ...(splitLabels ? { splitLabel: splitLabels[i - 1] } : {}),
            };

            if (useZebra) {
              // Use saved printer config for proper DPI/centering
              const printerConfig = loadSavedPrinterConfig();
              const zpl = generateVialLabelZpl(labelData, printerConfig);
              const result = await zebraPrint('vial', zpl);
              if (!result.success) {
                throw new Error(result.error || 'Zebra print failed');
              }
            } else {
              printThermalLabel(labelData);
            }

            // Small delay between prints
            await new Promise(resolve => setTimeout(resolve, 500));
          }

          setPrintStatus(prev => ({ ...prev, [`vial-${index}`]: true }));
        }
      }

      const printMethodText = useZebra ? 'Zebra printer' : 'browser';
      setSuccess(`${medications.length} thermal label(s) sent to ${printMethodText}!`);
    } catch (err: any) {
      console.error('Failed to print all labels:', err);
      setError(err.message || 'Failed to print all labels');
    } finally {
      setLoading(false);
    }
  };

  const handlePrintReceipt = async (medication: any, index: number) => {
    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      // Fetch receipt data from Kroll
      const receiptData = await fetchReceiptDataFromKroll(medication.rxNumber);

      if (!receiptData) {
        throw new Error('No receipt data found for this Rx number');
      }

      // Add pharmacyId for dynamic pharmacy info lookup and delivery info for receipt
      const receiptDataWithPharmacy: ThermalReceiptData = {
        ...receiptData,
        pharmacyId: activePharmacy?.id,
        deliveryType,
        shipmentDate,
        // GP-1008: carry the original product verifier's initials/name onto the
        // reprint (GP-654). Kroll has no verifier, so without this the reprinted
        // receipt drops the signature line the product-verified original showed.
        // This Rx's own verifier wins — a sibling Rx may have been verified by
        // someone else, and their name on this receipt would simply be wrong.
        productVerifierName: medication.productVerifierName ?? productVerifierName,
      };

      // Wait for Zebra initialization to complete before checking readiness
      const useZebra = await waitForZebraReady('receipt');

      if (!useZebra) {
        console.warn('[Print] Zebra not ready for receipt, falling back to browser.', {
          enabled: zebraEnabled,
          browserPrintAvailable,
          printerAssigned: !!getPrinter('receipt'),
          zebraInitialized,
        });
      }

      if (useZebra) {
        // Use Zebra Browser Print with dynamic insurance plan names
        const receiptFonts = loadSavedPrinterConfig().fonts;
        const zpl = generateReceiptZpl(receiptDataWithPharmacy, receiptFonts);
        const result = await zebraPrint('receipt', zpl);
        if (!result.success) {
          throw new Error(result.error || 'Zebra print failed');
        }
      } else {
        // Fall back to browser print
        printThermalReceipt(receiptDataWithPharmacy);
      }

      setPrintStatus(prev => ({ ...prev, [`receipt-${index}`]: true }));
      const printMethodText = useZebra ? 'Zebra printer' : 'browser';
      setSuccess(`Thermal receipt sent to ${printMethodText}!`);
    } catch (err: any) {
      console.error('Failed to generate thermal receipt:', err);
      setError(err.message || 'Failed to generate thermal receipt');
    } finally {
      setLoading(false);
    }
  };

  const handlePrintDeliveryLabel = async () => {
    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      // Get the first medication's Rx number to fetch address data from Kroll
      const rxNumber = medications.length > 0 ? medications[0].rxNumber : '';

      if (!rxNumber) {
        throw new Error('No Rx number available');
      }

      // Fetch receipt data from Kroll to get patient address
      const receiptData = await fetchReceiptDataFromKroll(rxNumber);

      if (!receiptData) {
        throw new Error('Failed to fetch patient address from Kroll');
      }

      // Extract first and last name from patient name - handle "LastName, FirstName" format from Kroll
      const fullName = receiptData.patientName || patientName;
      let firstName = '';
      let lastName = '';

      if (fullName.includes(',')) {
        // Format: "LastName, FirstName" - split by comma
        const parts = fullName.split(',');
        lastName = parts[0]?.trim() || '';
        firstName = parts[1]?.trim() || '';
      } else {
        // Format: "FirstName LastName" - split by space
        const parts = fullName.split(' ');
        firstName = parts[0] || '';
        lastName = parts.slice(1).join(' ') || parts[0] || '';
      }

      // GP-415: Use order's delivery address snapshot instead of Kroll patient address.
      // Kroll returns the patient's default/first address, which may differ from the
      // address selected for this specific order.
      // GP-564: `unit` flows through so the label can render "Unit X - street".
      // Fall back to parsing Kroll address only if deliveryAddress prop is not available.
      let address: { street: string; unit?: string; city: string; province: string; postalCode: string };
      if (deliveryAddress) {
        address = deliveryAddress;
      } else {
        // Fallback: parse from Kroll receipt data (Kroll receipt doesn't expose unit separately)
        const addressLine2Parts = receiptData.address2.split(',').map(s => s.trim());
        const city = addressLine2Parts[0] || '';
        const provincePostal = addressLine2Parts[1] || '';
        const provincePostalParts = provincePostal.split(' ').filter(s => s);
        const province = provincePostalParts[0] || '';
        const postalCode = provincePostalParts.slice(1).join(' ') || '';
        address = { street: receiptData.address1, city, province, postalCode };
      }

      const deliveryData: DeliveryLabelData = {
        orderNumber,
        patientFirstName: firstName,
        patientLastName: lastName,
        patientPhone: receiptData.patientPhone || '', // Patient phone number
        rxNumber: rxNumber,
        address,
        rxCount: medications.length,
        isNarcotic: medications.some(m => m.rxNumber?.startsWith('9')),
        // GP-1234: carry the fulfillment type onto the label so a pickup bag is
        // distinguishable from a courier run at the handoff station.
        deliveryMethod,
        deliveryType,
        shipmentDate,
        // GP-521/GP-664/GP-1317: the nursing-home name, so a tag printed from this
        // dialog sorts by home exactly like the auto-printed one. Stays undefined
        // for non-NH orders — the label then renders byte-identically to before.
        nhGroupName,
        // GP-887: second address line (absent on the Kroll-parse fallback path)
        deliveryInstructions: deliveryAddress?.deliveryInstructions || undefined,
      };

      // Wait for Zebra initialization to complete before checking readiness
      const useZebra = await waitForZebraReady('delivery');

      if (!useZebra) {
        console.warn('[Print] Zebra not ready for delivery, falling back to browser.', {
          enabled: zebraEnabled,
          browserPrintAvailable,
          printerAssigned: !!getPrinter('delivery'),
          zebraInitialized,
        });
      }

      if (useZebra) {
        // Use Zebra Browser Print
        const deliveryFonts = loadSavedPrinterConfig().fonts;
        const zpl = generateDeliveryLabelZpl(deliveryData, deliveryFonts);
        const result = await zebraPrint('delivery', zpl);
        if (!result.success) {
          throw new Error(result.error || 'Zebra print failed');
        }
      } else {
        // Fall back to browser print
        printDeliveryLabel(deliveryData);
      }

      setPrintStatus(prev => ({ ...prev, delivery: true }));
      const printMethodText = useZebra ? 'Zebra printer' : 'browser';
      setSuccess(`${labelHeading} sent to ${printMethodText}!`);
    } catch (err: any) {
      console.error('Failed to generate delivery label:', err);
      setError(err.message || 'Failed to generate delivery label');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box display="flex" alignItems="center" gap={2}>
            <Typography variant="h6">
              <PrintIcon sx={{ mr: 1, verticalAlign: 'bottom' }} />
              Label Printing - Order #{orderNumber}
            </Typography>
            {zebraEnabled && !zebraInitialized && (
              <Chip
                size="small"
                label="Checking Zebra..."
                color="warning"
                variant="outlined"
                icon={<CircularProgress size={12} />}
              />
            )}
            {zebraEnabled && zebraInitialized && (
              <Chip
                size="small"
                label={printMode === 'zebra' ? 'Zebra Mode' : 'Browser Mode'}
                color={printMode === 'zebra' ? 'success' : 'default'}
                variant="outlined"
              />
            )}
          </Box>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent>
        <Stack spacing={2}>
          {error && <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>}
          {success && <Alert severity="success" onClose={() => setSuccess(null)}>{success}</Alert>}

          {/* Tabs for different label types */}
          <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Tabs value={activeTab} onChange={(_, v) => setActiveTab(v)}>
              <Tab
                label={
                  <Box display="flex" alignItems="center">
                    <MedicationIcon sx={{ mr: 1 }} />
                    Vial Labels ({medications.length})
                  </Box>
                }
              />
              <Tab
                label={
                  <Box display="flex" alignItems="center">
                    <ReceiptIcon sx={{ mr: 1 }} />
                    Receipt
                  </Box>
                }
              />
              <Tab
                label={
                  <Box display="flex" alignItems="center">
                    <DeliveryIcon sx={{ mr: 1 }} />
                    {/* GP-1234: reads "Pickup Label" on a pickup order — a
                        "Delivery Label" tab is actively misleading there. */}
                    {labelHeading}
                  </Box>
                }
              />
            </Tabs>
          </Box>

          {/* Tab Content */}
          <Box sx={{ minHeight: 300 }}>
            {activeTab === 0 && (
              <Stack spacing={2}>
                {medications.length === 0 ? (
                  <Alert severity="info">No medications to print labels for</Alert>
                ) : (
                  medications.map((med, index) => (
                    <Paper key={index} sx={{ p: 2 }}>
                      <Grid container spacing={2} alignItems="center">
                        <Grid item xs={5}>
                          <Typography variant="subtitle1" fontWeight="bold">
                            {med.medication} {med.strength}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            RX: {med.rxNumber} | Qty: {med.quantity}
                          </Typography>
                          <Typography variant="body2" color="text.secondary" sx={{ 
                            overflow: 'hidden', 
                            textOverflow: 'ellipsis',
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical'
                          }}>
                            {med.directions}
                          </Typography>
                        </Grid>
                        <Grid item xs={3} textAlign="center">
                          <TextField
                            type="number"
                            size="small"
                            label="# Labels"
                            value={labelCountOverrides[index] || 1}
                            onChange={(e) => {
                              const val = parseInt(e.target.value) || 1;
                              if (!med.labelSplit) {
                                setLabelCountOverrides(prev => ({ ...prev, [index]: Math.max(1, val) }));
                                return;
                              }
                              // PRX-18: a split drug's count is capped so that no label prints 0,
                              // and the change goes through Packaging's audited count.
                              const count = Math.min(maxLabelsFor(med), Math.max(1, val));
                              setLabelCountOverrides(prev => ({ ...prev, [index]: count }));
                              onLabelCountChange?.(med.rxNumber, count);
                            }}
                            inputProps={{ min: 1, max: maxLabelsFor(med), style: { textAlign: 'center' } }}
                            sx={{ width: 80 }}
                          />
                          {med.labelSplit && (labelCountOverrides[index] || 1) >= maxLabelsFor(med) && maxLabelsFor(med) < MAX_VIAL_LABELS && (
                            <Typography variant="caption" color="text.secondary" display="block">
                              At most {maxLabelsFor(med)} labels, so that no label prints 0.
                            </Typography>
                          )}
                        </Grid>
                        <Grid item xs={4} textAlign="right">
                          {printStatus[`vial-${index}`] && (
                            <Chip
                              icon={<CheckIcon />}
                              label="Generated"
                              color="success"
                              size="small"
                              sx={{ mr: 1 }}
                            />
                          )}
                          <Button
                            variant="contained"
                            size="small"
                            startIcon={<ThermalIcon />}
                            onClick={() => handleGenerateVialLabel(med, index)}
                            disabled={loading}
                          >
                            Print ({labelCountOverrides[index] || 1})
                          </Button>
                        </Grid>
                      </Grid>
                    </Paper>
                  ))
                )}
              </Stack>
            )}

            {activeTab === 1 && (
              <Stack spacing={2}>
                {medications.length === 0 ? (
                  <Alert severity="info">No medications to print receipts for</Alert>
                ) : (
                  medications.map((med, index) => (
                    <Paper key={index} sx={{ p: 2 }}>
                      <Grid container spacing={2} alignItems="center">
                        <Grid item xs={8}>
                          <Typography variant="subtitle1" fontWeight="bold">
                            Receipt for Rx: {med.rxNumber}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {med.medication} {med.strength}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Patient: {patientName}
                          </Typography>
                        </Grid>
                        <Grid item xs={4} textAlign="right">
                          {printStatus[`receipt-${index}`] && (
                            <Chip
                              icon={<CheckIcon />}
                              label="Printed"
                              color="success"
                              size="small"
                              sx={{ mr: 1 }}
                            />
                          )}
                          <Button
                            variant="contained"
                            size="small"
                            startIcon={<ReceiptIcon />}
                            onClick={() => handlePrintReceipt(med, index)}
                            disabled={loading}
                          >
                            Print Receipt
                          </Button>
                        </Grid>
                      </Grid>
                    </Paper>
                  ))
                )}
              </Stack>
            )}

            {activeTab === 2 && (
              <Paper sx={{ p: 3 }}>
                <Stack spacing={2}>
                  <Typography variant="subtitle1" fontWeight="bold">
                    {labelHeading} (1-1/8" x 3-1/2")
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Compact label with the {fulfillment === 'PICKUP' ? 'pickup designation' : 'fulfillment type'},
                    patient name, address, and order barcode
                  </Typography>
                  {/* GP-1234: the fulfillment type the label will carry, read from
                      the same order field as the Order History "Delivery" column.
                      Omitted (not defaulted) when the order doesn't state one. */}
                  {fulfillment && (
                    <Box>
                      <Chip
                        label={fulfillment}
                        color={fulfillment === 'PICKUP' ? 'warning' : 'default'}
                        sx={{ fontWeight: 'bold', letterSpacing: 0.5 }}
                      />
                    </Box>
                  )}
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      {fulfillment === 'PICKUP' ? 'Patient (picking up in store):' : 'Recipient:'}
                    </Typography>
                    <Typography variant="body1" fontWeight="bold">{patientName}</Typography>
                    <Typography variant="body2">
                      {deliveryAddress?.street || ''}
                    </Typography>
                    <Typography variant="body2">
                      {deliveryAddress?.city || ''}{deliveryAddress?.city && deliveryAddress?.province ? ', ' : ''}{deliveryAddress?.province || ''} {deliveryAddress?.postalCode || ''}
                    </Typography>
                  </Box>
                  <Box textAlign="center" mt={2}>
                    {printStatus.delivery && (
                      <Chip
                        icon={<CheckIcon />}
                        label="Printed"
                        color="success"
                        size="small"
                        sx={{ mb: 2 }}
                      />
                    )}
                    <Box>
                      <Button
                        variant="contained"
                        size="large"
                        startIcon={<DeliveryIcon />}
                        onClick={handlePrintDeliveryLabel}
                        disabled={loading}
                      >
                        Print {labelHeading}
                      </Button>
                    </Box>
                  </Box>
                </Stack>
              </Paper>
            )}
          </Box>
        </Stack>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Close</Button>
        {medications.length > 1 && (
          <Button
            variant="contained"
            color="secondary"
            onClick={handlePrintAllLabels}
            disabled={loading}
            startIcon={loading ? <CircularProgress size={20} /> : <ThermalIcon />}
          >
            Print All Thermal Labels
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default LabelPrintingModal;